/**
 * Simple markdown-to-HTML renderer.
 * Handles: headings, bold, italic, lists, blockquotes, paragraphs.
 * No external dependencies.
 */
/**
 * Strip any leading machine-metadata block from report content. The model is
 * sometimes asked (or decides) to emit a YAML frontmatter block at the top of
 * a Summary — either bare (`---\n…\n---`) or wrapped in a ```yaml code fence.
 * It is indexing-only metadata and must never appear in the rendered view,
 * downloads, or clipboard copies. Safe to run on already-clean content.
 */
export function cleanReportContent(text: string): string {
  return text
    // Leading ```yaml … ``` fenced block (the fence usually wraps a --- block).
    .replace(/^\s*```ya?ml[ \t]*\n[\s\S]*?\n```[ \t]*\n?/, "")
    // Leading bare --- … --- YAML frontmatter block.
    .replace(/^\s*---[ \t]*\n[\s\S]*?\n---[ \t]*\n?/, "");
}

export function renderMarkdown(text: string): string {
  const stripped = cleanReportContent(text);

  // Escape HTML entities first
  let html = stripped
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // Split into lines for block-level processing
  const lines = html.split("\n");
  const output: string[] = [];
  let inList = false;
  let inOrderedList = false;
  let listBuffer: string[] = [];

  const flushList = () => {
    if (listBuffer.length > 0) {
      const tag = inOrderedList ? "ol" : "ul";
      output.push(`<${tag}>${listBuffer.join("")}</${tag}>`);
      listBuffer = [];
      inList = false;
      inOrderedList = false;
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Headings
    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      flushList();
      const level = headingMatch[1].length;
      output.push(`<h${level}>${inlineMarkdown(headingMatch[2])}</h${level}>`);
      continue;
    }

    // Horizontal rule
    if (/^(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      flushList();
      output.push("<hr />");
      continue;
    }

    // Unordered list
    const ulMatch = line.match(/^(\s*)[*\-+]\s+(.+)$/);
    if (ulMatch) {
      if (!inList || inOrderedList) {
        flushList();
        inList = true;
        inOrderedList = false;
      }
      listBuffer.push(`<li>${inlineMarkdown(ulMatch[2])}</li>`);
      continue;
    }

    // Ordered list
    const olMatch = line.match(/^(\s*)\d+\.\s+(.+)$/);
    if (olMatch) {
      if (!inList || !inOrderedList) {
        flushList();
        inList = true;
        inOrderedList = true;
      }
      listBuffer.push(`<li>${inlineMarkdown(olMatch[2])}</li>`);
      continue;
    }

    // Blockquote
    const bqMatch = line.match(/^&gt;\s?(.*)$/);
    if (bqMatch) {
      flushList();
      output.push(`<blockquote><p>${inlineMarkdown(bqMatch[1])}</p></blockquote>`);
      continue;
    }

    // Empty line
    if (line.trim() === "") {
      flushList();
      continue;
    }

    // Regular paragraph
    flushList();
    output.push(`<p>${inlineMarkdown(line)}</p>`);
  }

  flushList();
  return output.join("\n");
}

function inlineMarkdown(text: string): string {
  return (
    text
      // Bold + italic
      .replace(/\*\*\*(.+?)\*\*\*/g, "<strong><em>$1</em></strong>")
      // Bold
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      // Italic
      .replace(/\*(.+?)\*/g, "<em>$1</em>")
      // Inline code
      .replace(/`(.+?)`/g, "<code>$1</code>")
  );
}
